import { useCallback, useEffect, useRef, useState } from 'react';
import { InboxService } from '@rsmaids/shared/services/api/inboxService';
import { parseWixMessage } from '../services/messageParser';
import { StaffCacheService } from '@rsmaids/shared/services/api/staffCacheService';
import { applyStaffLookup } from '../services/messageEnrichment';
import { auth } from '../auth/firebase';

// Local UI Message type (all channel types for display/receive, send restricted in components)
interface Message {
  id?: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email' | 'call';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean;
  avatarUrl?: string;
  extractedStaffInfo?: { staffName: string; avatarUrl?: string };
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  media?: Array<{
    url?: string;
    type: 'image' | 'video' | 'file' | 'audio' | 'icon';
    name?: string;
    iconName?: string;
    duration?: number;
  }>;
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
  _callData?: {
    callId: string;
    direction: 'inbound' | 'outbound';
    duration: number;
    recordingUrl?: string;
    callerName?: string;
    customerNumber: string;
  };
  _smsData?: {
    direction: 'inbound' | 'outbound';
    customerNumber: string;
  };
}

export interface UseConversationMessagesOptions {
  id: string;
  contactName: string;
  contactAvatarUrl?: string;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  hasMoreOlderMessages: boolean;
  setHasMoreOlderMessages: (hasMore: boolean) => void;
  seenMessageIdsRef: React.MutableRefObject<Set<string>>;
  initialLoadDone: boolean;
  messagesRef: React.MutableRefObject<Message[]>;
}

export interface UseConversationMessagesResult {
  loadingOlderMessages: boolean;
  handleLoadOlderMessages: () => Promise<void>;
}

/**
 * Hook to manage message pagination and prefetching
 * ONLY handles pagination - initial load and subscriptions are handled by other hooks
 */
export const useConversationMessages = ({
  id,
  contactName,
  contactAvatarUrl,
  setMessages,
  hasMoreOlderMessages,
  setHasMoreOlderMessages,
  seenMessageIdsRef,
  messagesRef,
}: UseConversationMessagesOptions): UseConversationMessagesResult => {
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  // Refs for managing pagination state
  const isLoadingOlderRef = useRef(false);
  const paginationCursorRef = useRef<string | undefined>(undefined);
  const messageQueueRef = useRef<Array<{ messages: unknown[]; nextCursor?: string }>>([]);
  const isPrefetchingRef = useRef(false);
  const firstPaginationDoneRef = useRef(false);
  const hasMoreOlderMessagesRef = useRef(false);

  // Initialize hasMore ref from prop
  useEffect(() => {
    hasMoreOlderMessagesRef.current = hasMoreOlderMessages;
  }, [hasMoreOlderMessages]);

  // CRITICAL: Reset all pagination refs when conversation changes
  useEffect(() => {
    paginationCursorRef.current = undefined;
    messageQueueRef.current = [];
    isPrefetchingRef.current = false;
    firstPaginationDoneRef.current = false;
    hasMoreOlderMessagesRef.current = false;
    console.log(`🔄 Reset pagination state for conversation: ${id}`);
  }, [id]);

  // Load older messages when user scrolls to top
  const handleLoadOlderMessages = useCallback(async (): Promise<void> => {
    if (isLoadingOlderRef.current || !hasMoreOlderMessagesRef.current) {
      console.log(
        `⏭️ Cannot load older: isLoading=${isLoadingOlderRef.current}, hasMore=${hasMoreOlderMessagesRef.current}`
      );
      return;
    }

    // SMS conversations: fetch older messages from Twilio API
    if (id.startsWith('sms-')) {
      isLoadingOlderRef.current = true;
      setLoadingOlderMessages(true);
      try {
        const phone = `+${id.replace('sms-', '')}`;
        const before = paginationCursorRef.current || (() => {
          const oldest = messagesRef.current[0];
          if (!oldest) return undefined;
          const d = oldest._createdDate;
          return d instanceof Date ? d.toISOString() : typeof d === 'string' ? d : undefined;
        })();
        if (!before) { setLoadingOlderMessages(false); isLoadingOlderRef.current = false; return; }

        const currentUser = auth.currentUser;
        if (!currentUser) { setLoadingOlderMessages(false); isLoadingOlderRef.current = false; return; }
        const token = await currentUser.getIdToken();
        const baseUrl = (import.meta as { env: Record<string, string> }).env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-rsmaidsbooking.cloudfunctions.net';
        const url = `${baseUrl}/getOlderSmsMessages?phone=${encodeURIComponent(phone)}&before=${encodeURIComponent(before)}&limit=30`;

        const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) { console.error(`[SMS pagination] HTTP ${response.status}`); setLoadingOlderMessages(false); isLoadingOlderRef.current = false; return; }

        const data = await response.json() as { messages: Array<{ id: string; body: string; direction: string; timestamp: number; from: string; to: string }>; nextCursor?: string };
        const { messages: rawMessages, nextCursor } = data;

        if (nextCursor) {
          paginationCursorRef.current = nextCursor;
          hasMoreOlderMessagesRef.current = true;
        } else {
          hasMoreOlderMessagesRef.current = false;
          setHasMoreOlderMessages(false);
        }

        if (rawMessages.length > 0) {
          const smsMessages: Message[] = rawMessages
            .map(m => ({
              id: m.id,
              text: m.body,
              from: m.direction === 'inbound' ? contactName || phone : 'You',
              channel: 'sms' as const,
              _createdDate: new Date(m.timestamp),
              isFromCustomer: m.direction === 'inbound',
              direction: m.direction,
            }))
            .sort((a, b) => a._createdDate.getTime() - b._createdDate.getTime()); // oldest first

          const newMessages = smsMessages.filter(m => !m.id || !seenMessageIdsRef.current.has(m.id));
          newMessages.forEach(m => { if (m.id) seenMessageIdsRef.current.add(m.id); });

          if (newMessages.length > 0) {
            setMessages(prev => [...newMessages as Message[], ...prev] as Message[]);
            messagesRef.current = [...newMessages as Message[], ...messagesRef.current] as Message[];
            if (paginationCursorRef.current) {
              setHasMoreOlderMessages(true);
              hasMoreOlderMessagesRef.current = true;
            }
          }
        }
      } catch (error) {
        console.error(`❌ Error loading older SMS messages:`, error);
      } finally {
        setLoadingOlderMessages(false);
        isLoadingOlderRef.current = false;
      }
      return;
    }

    try {
      isLoadingOlderRef.current = true;
      setLoadingOlderMessages(true);
      console.log(`📂 handleLoadOlderMessages: firstPaginationDone=${firstPaginationDoneRef.current}, cursor="${paginationCursorRef.current || 'undefined'}"`);

      let olderMessages: Array<Record<string, unknown>>;
      let usesPrefetch = false;
      let prefetchedNextCursor: string | undefined;

      // Check if we have prefetched messages from previous pagination
      if (messageQueueRef.current.length > 0) {
        const prefetched = messageQueueRef.current.shift()!;
        olderMessages = prefetched.messages as unknown as Array<Record<string, unknown>>;
        prefetchedNextCursor = prefetched.nextCursor as string | undefined;
        usesPrefetch = true;
        console.log(`⚡ Using ${olderMessages.length} prefetched messages from queue`);
        // CRITICAL: Update cursor from prefetched data for next pagination
        if (prefetchedNextCursor) {
          paginationCursorRef.current = prefetchedNextCursor;
          console.log(`📍 Updated cursor from prefetch: ${paginationCursorRef.current}`);
        }
      } else {
        // Fetch from Wix API if no prefetch available
        console.log(`🔄 [CRITICAL] Fetching from API: firstPaginationDone=${firstPaginationDoneRef.current}, cursor=${paginationCursorRef.current || 'NONE'}`);
        const result: Record<string, unknown> = await InboxService.getMessagesNextPage(
          id,
          30,
          paginationCursorRef.current
        );
        olderMessages = (result.messages as unknown as Array<Record<string, unknown>>) || [];
        console.log(`🔄 [RESPONSE] Got ${olderMessages.length} messages from API. First message ID: ${((olderMessages[0] as Record<string, unknown>)?.msgId as string | undefined) || 'N/A'}`);

        if (result.nextCursor) {
          paginationCursorRef.current = result.nextCursor as string;
          hasMoreOlderMessagesRef.current = true;
          console.log(`📍 Next cursor: ${paginationCursorRef.current}`);
        } else {
          hasMoreOlderMessagesRef.current = false;
          setHasMoreOlderMessages(false);
          console.log(`📭 No more messages`);
        }
      }

      // Update cursor from prefetch if available
      if (usesPrefetch && prefetchedNextCursor) {
        paginationCursorRef.current = prefetchedNextCursor;
        hasMoreOlderMessagesRef.current = true;
        console.log(`📍 Updated cursor from prefetch: ${paginationCursorRef.current}`);
      } else if (usesPrefetch && !prefetchedNextCursor) {
        // Prefetch was used but no next cursor - no more messages
        hasMoreOlderMessagesRef.current = false;
        setHasMoreOlderMessages(false);
        console.log(`📭 Prefetch used but no next cursor - no more messages`);
      }

      // Mark first pagination as complete
      if (!firstPaginationDoneRef.current && olderMessages.length > 0) {
        firstPaginationDoneRef.current = true;
        console.log(`✅ First pagination complete - flag now TRUE`);
      }

      // Start prefetching NEXT page in background (always do this after getting messages)
      if (firstPaginationDoneRef.current && hasMoreOlderMessagesRef.current && !isPrefetchingRef.current && paginationCursorRef.current) {
        console.log(`🔮 Starting background prefetch for next page with cursor: ${paginationCursorRef.current}`);
        isPrefetchingRef.current = true;
        // Prefetch uses current cursor to get next page
        InboxService.getMessagesNextPage(id, 30, paginationCursorRef.current)
          .then((result: Record<string, unknown>) => {
            if (result.messages && (result.messages as unknown[]).length > 0) {
              const nextCursor = result.nextCursor as string | undefined;
              messageQueueRef.current.push({ 
                messages: result.messages as unknown[],
                nextCursor
              });
              console.log(`🔮 Prefetched: ${(result.messages as unknown[]).length} messages with cursor: ${nextCursor}`);
            }
            isPrefetchingRef.current = false;
          })
          .catch((prefetchErr: Error) => {
            console.error(`❌ Background prefetch failed:`, prefetchErr);
            isPrefetchingRef.current = false;
          });
      }

      if (olderMessages.length === 0) {
        console.log(`📭 No older messages returned`);
        setLoadingOlderMessages(false);
        setHasMoreOlderMessages(false);
        hasMoreOlderMessagesRef.current = false;
        return;
      }

      // Parse and reverse older messages
      const parsedOlderMessages: Message[] = olderMessages.map((msg: Record<string, unknown>) =>
        parseWixMessage(msg, contactName || 'Contact') as unknown as Message
      );
      
      // Staff name/avatar arrive already stamped by the server (getMessages), and the local staff cache
      // covers the rest — so each older message is complete the moment it is added, with no lookups.
      const staffCache = StaffCacheService.getInstance();
      const enrichedOlderMessages = parsedOlderMessages.map((msg) => {
        // Skip only if the message already has complete enrichment (senderStaffId + senderStaffAvatar)
        if (msg.senderStaffId && msg.senderStaffAvatar) return msg;

        // Enrich all other messages - enrichment will handle customer vs staff determination
        const enrichedMsg = applyStaffLookup(msg, staffCache);

        // For customer messages, inject contact avatar if not already set
        if (enrichedMsg.isFromCustomer && contactAvatarUrl && !enrichedMsg.avatarUrl) {
          enrichedMsg.avatarUrl = contactAvatarUrl;
        }
        return enrichedMsg;
      });

      const reversedOlder = enrichedOlderMessages.reverse();

      // Filter out duplicates before prepending
      console.log(`🔍 [DEDUPE CHECK] seenMessageIds has ${seenMessageIdsRef.current.size} IDs. First 3 cached IDs: ${Array.from(seenMessageIdsRef.current).slice(0, 3).join(', ')}`);
      console.log(`🔍 [DEDUPE CHECK] Incoming older messages: ${reversedOlder.length} items. First 3 IDs: ${reversedOlder.slice(0, 3).map(m => m.id).join(', ')}`);
      
      const newMessages = reversedOlder.filter(msg => {
        const isDuplicate = msg.id && seenMessageIdsRef.current.has(msg.id);
        if (isDuplicate) {
          console.log(`🔄 Skipping duplicate: ${msg.id}`);
        }
        return !isDuplicate;
      });

      // Track new message IDs
      newMessages.forEach(m => {
        if (m.id) seenMessageIdsRef.current.add(m.id);
      });

      // Prepend older messages to the array
      if (newMessages.length > 0) {
        // Update state and ref - prepend older messages to the array
        setMessages((prev) => [...newMessages as Message[], ...prev] as Message[]);
        messagesRef.current = [...newMessages as Message[], ...messagesRef.current] as Message[];

        console.log(`✅ Added ${newMessages.length} new older messages (${reversedOlder.length} - ${reversedOlder.length - newMessages.length} duplicates)`);
        
        // UPDATE HASMORE STATE AFTER MESSAGES ARE ADDED - important for infinite scroll re-enabling
        // Check if we have a cursor available (already set in paginationCursorRef from API or prefetch)
        if (paginationCursorRef.current) {
          console.log(`📞 About to call setHasMoreOlderMessages(true) with cursor: ${paginationCursorRef.current}`);
          
          // Simply set to true - don't toggle
          setHasMoreOlderMessages(true);
          
          // CRITICAL: Update ref immediately - don't wait for useEffect!
          hasMoreOlderMessagesRef.current = true;
          console.log(`✅ Re-enabling infinite scroll - cursor available: ${paginationCursorRef.current}`);
          
          // Clear loading flag after a short delay to ensure state updates are processed
          setTimeout(() => {
            console.log(`🏁 Setting loadingOlderMessages to false`);
            setLoadingOlderMessages(false);
            isLoadingOlderRef.current = false;
          }, 50);
        } else {
          console.log(`⚠️ No cursor available - NOT re-enabling infinite scroll`);
          console.log(`🏁 Setting loadingOlderMessages to false`);
          setLoadingOlderMessages(false);
          isLoadingOlderRef.current = false;
        }
      } else {
        console.log(`✅ All ${reversedOlder.length} messages were duplicates`);
        // Still clear loading flag even if no new messages
        setLoadingOlderMessages(false);
        isLoadingOlderRef.current = false;
      }
    } catch (error) {
      console.error(`❌ Error loading older messages:`, error);
      // Clear loading flag on error too
      setLoadingOlderMessages(false);
      isLoadingOlderRef.current = false;
    }
  }, [id, contactName, contactAvatarUrl, setMessages, setHasMoreOlderMessages, seenMessageIdsRef, messagesRef]);

  return {
    loadingOlderMessages,
    handleLoadOlderMessages,
  };
};
